import * as React from 'react'
import { Form, Input, Tabs, Spin, Button, Dropdown, Menu, message } from 'antd'
import { set, get } from 'lodash'
import produce from 'immer'
import Select, { Option } from 'elements/Select'
import Checkbox from 'elements/Checkbox'
import CheckboxList from 'elements/CheckboxList'
import InputNumber from 'elements/InputNumber'
import { Row, Col } from 'elements/Grid'
import { showError, resourceNotFound, ValidationError, showClientNotifications } from 'helpers/errors'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  validateBarcode,
  isReadOnly,
} from 'helpers/formViews'
import { getUserPermission } from 'helpers/auth'
import cartTypes from 'options/inventoryCartTypes'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import FormButtons from 'elements/FormButtons'
import Icon from 'elements/Icon'

const locationVendingTypes = {
  Primary: 'primary',
  Secondary: 'secondary',
  Vending: 'vending',
  Inactive: 'inactive',
}

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
  }

  transformSaveItemParams = ({ params }) => ({
    minPar: params.cartType !== 'Par' ? 'false' : params.minPar,
  })

  componentDidMount() {
    this.fetchItem()
  }

  getItemId = () => {
    if (this.state.item?.id) {
      return this.state.item.id
    }

    if (this.props.isFromLocation) {
      return this.props.linkTargetRecord?.fromLocationId
    }

    if (this.props.isToLocation) {
      return this.props.linkTargetRecord?.toLocationId
    }

    return [this.props.linkTargetRecord?.locationId, this.props.linkTargetRecord?.jobTaskSettings?.locationId].find(
      Boolean,
    )
  }

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
        this.props.getLocationGroups({ includeAllLocationGroups: true }),
      ])

      this.setState({
        item: responses[0].value.data,
        fieldSettings: responses[1].value.data.fieldSettings,
        locationGroups: get(responses[2], 'value.data.items', []),
      })
    } catch (error) {
      showError({ error })
    }
  }

  saveItemSuccess = async (savedItem, saveItemParams) => {
    const response = !saveItemParams.id
      ? await this.props.sendCreateNotification({ id: savedItem.id })
      : await this.props.sendUpdateNotification({ id: savedItem.id })

    showClientNotifications({ response })

    return response
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
    )

  handleActionsMenuClick = async ({ key }) => {
    if (key === 'generateTopUpOrder') {
      try {
        await this.props.onSave?.(true)

        const response = await this.props.generateTopUpOrder(this.state.item)

        showClientNotifications({ response })
      } catch (error) {
        showError({ error })
      } finally {
        await this.props.onSave?.(false)
      }
    } else {
      message.warn(t('underDevelopment'))
    }
  }

  render() {
    const { tenantGroupIsCardinal } = this.props
    const { item, fieldSettings, locationGroups } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly =
      isReadOnly(this) || (getUserPermission('Master') !== 'Yes' && getUserPermission('Locations') === 'No')

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        {tenantGroupIsCardinal && item.id ? (
          <Dropdown
            overlay={
              <Menu onClick={this.handleActionsMenuClick} disabled={!item.id}>
                <Menu.Item key="generateTopUpOrder">{t('generateTopUpOrder')}</Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button className="form-items-actions">
              {t('actions')}
              <Icon type="KeyboardArrowDown" />
            </Button>
          </Dropdown>
        ) : null}
        <div className="form-items-container">
          <ValidationError errors={this.state.validationErrors} />
          <Row>
            <Col md={12} xs={24}>
              <Form.Item label={createLabel('name')}>
                {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus />)}
              </Form.Item>
              <Form.Item label={createLabel('barcode')}>
                {createFieldDecorator('barcode', { extraRules: [validateBarcode()] })(<Input readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('description')}>
                {createFieldDecorator('description')(<Input readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('locationType')}>
                {createFieldDecorator('locationType')(
                  <Select allowClear={false} readOnly={readOnly}>
                    {Object.entries(locationVendingTypes).map(([key, value]) => (
                      <Option key={key} value={key}>
                        {t(value)}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={createLabel('misc1')}>
                {createFieldDecorator('misc1')(<Input readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('misc2')}>
                {createFieldDecorator('misc2')(<Input readOnly={readOnly} />)}
              </Form.Item>
              <Form.Item label={createLabel('taxRate')}>
                {createFieldDecorator('taxRate')(
                  <InputNumber readOnly={readOnly} allowSteppers={false} precision={2} />,
                )}
              </Form.Item>
              {tenantGroupIsCardinal && (
                <>
                  <Form.Item label={createLabel('cartType')}>
                    {createFieldDecorator('cartType')(
                      <Select
                        onChange={(value) => this.setItemValue('cartType', value)}
                        allowClear={false}
                        readOnly={readOnly}
                      >
                        {Object.entries(cartTypes).map(([key, value]) => (
                          <Option key={key} value={key}>
                            {t(value)}
                          </Option>
                        ))}
                      </Select>,
                    )}
                  </Form.Item>
                  {item.cartType === 'Par' && (
                    <Form.Item label=" ">
                      {createFieldDecorator('minPar', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly}>{createLabel('minPar')}</Checkbox>,
                      )}
                    </Form.Item>
                  )}
                </>
              )}
            </Col>
          </Row>
          <Tabs
            activeKey={this.state.tabsActiveKey || 'address'}
            onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
          >
            <Tabs.TabPane key="address" tab={t('address')} forceRender>
              <Row>
                <Col md={12} xs={24}>
                  <Form.Item label={createLabel('address.address1')}>
                    {createFieldDecorator('address.address1')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.address2')}>
                    {createFieldDecorator('address.address2')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.city')}>
                    {createFieldDecorator('address.city')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.state')}>
                    {createFieldDecorator('address.state')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.country')}>
                    {createFieldDecorator('address.country')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.zip')}>
                    {createFieldDecorator('address.zip')(<Input readOnly={readOnly} />)}
                  </Form.Item>
                </Col>
                <Col md={12} xs={24}>
                  <Form.Item label={createLabel('address.phone')}>
                    {createFieldDecorator('address.phone')(<Input type="tel" readOnly={readOnly} />)}
                  </Form.Item>
                  <Form.Item label={createLabel('address.fax')}>
                    {createFieldDecorator('address.fax')(<Input type="tel" readOnly={readOnly} />)}
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane key="locationGroups" tab={t('locationGroups')} forceRender>
              <Row>
                <Col md={12} xs={24}>
                  <Form.Item label={t('memberOf')}>
                    <CheckboxList
                      dataSource={locationGroups}
                      value={item.locationGroupIds}
                      onChange={(values) => this.setItemValue('locationGroupIds', values)}
                      readOnly={readOnly}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Tabs.TabPane>
          </Tabs>
        </div>
        <div className="form-buttons-container">
          <FormButtons
            readOnly={readOnly}
            onCancel={this.props.onCancel}
            onSubmit={this.handleSubmit}
            saveButtonLoading={this.state.saveButtonLoading}
            saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
          />
        </div>
      </Form>
    )
  }
}

export default Form.create()(FormView)
